import {
  CreateUserRequest,
  ProjectsService,
  Scope,
  UpdateUserRequest,
  UserDetails,
  UsersService,
} from '@beeboard/common';
import { defineStore } from 'pinia';
import { createConcurrencyQueue } from '@/utils/helpers/concurrencyQueue';
import {
  fetchProfilePictureBlobUrl,
  invalidateBlobCache,
} from '@/utils/helpers/userApiHelpers';
import { useProfileStore } from './profileStore';
import { useUserRulesStore } from './userRulesStore';
import type { UserSummary } from '@beeboard/common';

interface scopes {
  templateReader?: string[];
  templateCreator?: string[];
  projectReader?: string[];
  projectCreator?: string[];
}

const enqueueAvatarFetch = createConcurrencyQueue(10);

export type UserStoreState = {
  users: UserSummary[];
  usersDetails: UserDetails[];
  scopes: Scope[];
  avatarUrls: Record<string, string | null | undefined>;
};

export const useUserStore = defineStore('userStore', {
  state: (): UserStoreState => ({
    users: [],
    usersDetails: [],
    scopes: [],
    avatarUrls: {},
  }),

  getters: {
    user(state) {
      return (userId: string) => {
        const user = state.users.find((user) => user.id === userId);
        if (!user) {
          throw new Error(`User ${userId} not found`);
        }

        return user;
      };
    },

    scope(state) {
      return (scopeId: string) => {
        const scope = state.scopes.find((scope) => scope.id === scopeId);
        if (!scope) {
          throw new Error(`Scope ${scopeId} not found`);
        }

        return scope;
      };
    },

    hasScope(state) {
      return (scopeId: string) => {
        return state.scopes.some((scope) => scope.id === scopeId);
      };
    },

    userDetails(state) {
      return (userId: string) => {
        const user = state.usersDetails.find((user) => user.id === userId);
        if (!user) {
          throw new Error(`User ${userId} details not found`);
        }

        return user;
      };
    },

    hasUserDetails(state) {
      return (userId: string) => {
        return state.usersDetails.some((user) => user.id === userId);
      };
    },
  },

  actions: {
    writeUser(userSummary: UserSummary) {
      const oldUser = this.users.find((user) => user.id === userSummary.id);
      if (oldUser) {
        Object.assign(oldUser, userSummary);
      } else {
        this.users.push(userSummary);
      }

      return this.user(userSummary.id);
    },

    writeUserDetails(details: UserDetails) {
      const oldUser = this.usersDetails.find((user) => user.id === details.id);
      if (oldUser) {
        Object.assign(oldUser, details);
      } else {
        this.usersDetails.push(details);
      }

      return this.userDetails(details.id);
    },

    writeScope(scope: Scope) {
      const oldScope = this.scopes.find((sc) => sc.id === scope.id);
      if (oldScope) {
        Object.assign(oldScope, scope);
      } else {
        this.scopes.push(scope);
      }

      return this.scope(scope.id);
    },

    removeUserFromStore(userId: string) {
      const summaryIndex = this.users.findIndex((user) => user.id === userId);
      if (summaryIndex >= 0) {
        this.users.splice(summaryIndex, 1);
      }
      const detailsIndex = this.usersDetails.findIndex(
        (user) => user.id === userId,
      );
      if (detailsIndex >= 0) {
        this.usersDetails.splice(detailsIndex, 1);
      }
    },

    async createUser(payload: CreateUserRequest) {
      return UsersService.createUser(payload).then((details) => {
        this.writeUser(details);

        return this.writeUserDetails(details);
      });
    },

    async deleteUser(userId: string) {
      return UsersService.deleteUser(userId).then(() => {
        this.removeUserFromStore(userId);
      });
    },

    async loadUser(userId: string) {
      await UsersService.getUser(userId).then((user) => {
        return this.writeUser(user);
      });
    },

    async updateUser(userId: string, payload: UpdateUserRequest) {
      await UsersService.updateUser(userId, payload).then((user) => {
        this.writeUser(user);
        if (userId === useProfileStore().profile.id) {
          useProfileStore().writeProfile(user);
        }

        return this.writeUserDetails(user);
      });
    },

    async loadUsers({
      templateReader,
      templateCreator,
      projectReader,
      projectCreator,
    }: scopes) {
      const { users } = await UsersService.fetchUsers(
        templateReader,
        templateCreator,
        projectReader,
        projectCreator,
      );
      this.users = users;

      return this.users;
    },

    // A template owner needs template reader in every scope of the template. Without
    // scopes the API's `conjunction` filter matches nobody, so a template without
    // scopes is offered to everyone who reads templates in some scope.
    async fetchEligibleOwners(templateScopes: string[]) {
      const isAnyReader = !templateScopes.length;
      if (isAnyReader && !this.scopes.length) {
        await this.loadScopes();
      }
      const { users } = await UsersService.fetchUsers(
        isAnyReader ? this.scopes.map((scope) => scope.id) : templateScopes,
        undefined,
        undefined,
        undefined,
        !isAnyReader,
      );

      return users;
    },

    async loadUsersDetails() {
      const { users } = await UsersService.fetchUsersDetails();
      this.usersDetails = users;

      return this.users;
    },

    async loadUserProfile(userId: string) {
      return UsersService.getUser(userId).then((profile) => {
        return this.writeUserDetails(profile);
      });
    },

    async loadScopes() {
      return UsersService.getScopes().then((scopes) => {
        scopes.forEach(this.writeScope);
      });
    },

    async createScope(name: string) {
      const errors = useUserRulesStore().validateCreateScopeRequest(name);
      if (errors) {
        throw errors;
      }

      return UsersService.createScope({ name }).then(this.writeScope);
    },

    async editScope(scopeId: string, name: string) {
      const errors = useUserRulesStore().validateUpdateScopeRequest(
        name,
        scopeId,
      );
      if (errors) {
        throw errors;
      }

      return UsersService.updateScopes(scopeId, { name }).then((scope) =>
        this.writeScope(scope),
      );
    },

    async deleteScope(scopeId: string) {
      return UsersService.removeScope(scopeId).then(() => {
        const scopeIndex = this.scopes.findIndex(
          (scope) => scope.id === scopeId,
        );
        if (scopeIndex >= 0) {
          this.scopes.splice(scopeIndex, 1);
        }
      });
    },

    loadUserAvatar(userId: string, hasProfilePicture = true) {
      if (!hasProfilePicture) {
        this.avatarUrls[userId] = null;

        return;
      }
      if (this.avatarUrls[userId] !== undefined) {
        return;
      }
      this.avatarUrls[userId] = null; // marks as in-progress and no-image fallback
      enqueueAvatarFetch(async () => {
        this.avatarUrls[userId] = await fetchProfilePictureBlobUrl(
          userId,
        ).catch(() => null);
      });
    },

    setUserAvatar(userId: string, url: string) {
      const existing = this.avatarUrls[userId];
      if (existing) {
        URL.revokeObjectURL(existing);
      }
      this.avatarUrls[userId] = url || null;
      void invalidateBlobCache(`/v1/users/${userId}/profile-picture`);
    },

    revokeAllAvatarUrls() {
      for (const url of Object.values(this.avatarUrls)) {
        if (url) {
          URL.revokeObjectURL(url);
        }
      }
      this.avatarUrls = {};
    },

    sendPasswordLink(email: string) {
      return UsersService.resetPassword({ email });
    },

    async assignScopeToAllUsers(
      scopeId: string,
      categories: {
        projectReader?: boolean;
        projectCreator?: boolean;
        templateReader?: boolean;
        templateCreator?: boolean;
      },
    ) {
      const userIds = this.users.map((user) => user.id);
      if (userIds.length === 0) {
        throw new Error('No users available to assign scope to');
      }

      // Build the scope assignment based on selected categories
      const scopeAssignment = {
        project_reader: categories.projectReader ? [scopeId] : [],
        project_creator: categories.projectCreator ? [scopeId] : [],
        template_reader: categories.templateReader ? [scopeId] : [],
        template_creator: categories.templateCreator ? [scopeId] : [],
      };

      return UsersService.assignScopeToMultipleUsers({
        userIds,
        scope: {
          scopes: scopeAssignment,
        },
      }).then((result) => {
        // Update local state based on successful assignments
        if (result.successful && result.successful.length > 0) {
          // Refresh user data to reflect new scope assignments
          void this.loadUsers({});
        }

        return result;
      });
    },

    async updateUserProjectActivity(
      projectId: string,
      activity: { catch_up_read: boolean },
    ) {
      return ProjectsService.updateUserActivity(projectId, activity);
    },
  },
});
